import React, { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';
import { AppConfig, CONFIG_DEFAULTS } from '../types/config.types';
import {
  isArgentinaCountry,
  isUruguayCountry,
  normalizeTenantCountry,
} from '../utils/countryConfig';

interface ConfigContextValue {
  config: Required<Omit<AppConfig, 'financialIndicators' | 'isArgentinaEnabled'>> &
    Pick<AppConfig, 'financialIndicators'> & { isArgentinaEnabled: boolean };
  setConfig: (raw: Partial<AppConfig>) => void;
}

const readFromLocalStorage = (): Partial<AppConfig> => {
  try {
    return JSON.parse(localStorage.getItem('config') ?? '{}');
  } catch {
    return {};
  }
};

const normalizeLocalCurrencyCode = (raw: unknown): 'ARS' | 'UYU' => {
  const value = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  return value === 'UYU' ? 'UYU' : 'ARS';
};

const applyDefaults = (
  raw: Partial<AppConfig>,
): ConfigContextValue['config'] => {
  const merged = {
    ...CONFIG_DEFAULTS,
    ...raw,
  };
  const country = normalizeTenantCountry(raw.country);
  return {
    ...merged,
    localCurrencyCode: normalizeLocalCurrencyCode(merged.localCurrencyCode),
    country,
    isUruguayEnabled: isUruguayCountry(country),
    // Strict on raw country — normalize fallback must not unlock NOSIS.
    isArgentinaEnabled: isArgentinaCountry(raw.country),
  };
};

const ConfigContext = createContext<ConfigContextValue | null>(null);

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [raw, setRaw] = useState<Partial<AppConfig>>(readFromLocalStorage);

  const config = useMemo(() => applyDefaults(raw), [raw]);

  const setConfig = useCallback((incoming: Partial<AppConfig>) => {
    localStorage.setItem('config', JSON.stringify(incoming));
    setRaw(incoming);
  }, []);

  const value = useMemo(() => ({ config, setConfig }), [config, setConfig]);

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextValue['config'] => {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    throw new Error('useConfig debe usarse dentro de ConfigProvider');
  }
  return ctx.config;
};

export const useSetConfig = (): ConfigContextValue['setConfig'] => {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    throw new Error('useSetConfig debe usarse dentro de ConfigProvider');
  }
  return ctx.setConfig;
};
